import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

const { FiPhone, FiMessageCircle, FiMapPin, FiMail } = FiIcons;

const Footer = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleServiceClick = (e, href) => {
    e.preventDefault();

    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/' + href);
    } else {
      // If already on home, just scroll
      const id = href.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="col-span-2"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">B</span>
              </div>
              <div>
                <h3 className="text-xl font-bold">Bertoproduction</h3>
                <p className="text-gray-400 text-sm">Técnico Informático Mar Menor</p>
              </div>
            </div>
            <p className="text-gray-400 mb-6 max-w-md">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                onClick={() => window.open('https://wa.me/34642643499', '_blank')}
                className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center hover:bg-green-700 transition-colors"
                aria-label="WhatsApp"
              >
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                onClick={() => window.location.href = 'tel:+34642643499'}
                className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors"
                aria-label="Teléfono"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                onClick={() => window.location.href = 'mailto:info@tecnicoinformaticolamanga.com'}
                className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center hover:bg-purple-700 transition-colors"
                aria-label="Email"
              >
                <SafeIcon icon={FiMail} className="w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h4 className="text-lg font-semibold mb-4">{t('footer.services')}</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <a 
                  href="#services" 
                  onClick={(e) => handleServiceClick(e, '#services')}
                  className="hover:text-white transition-colors cursor-pointer"
                >
                  Reparación PC La Manga
                </a>
              </li>
              <li>
                <a 
                  href="#services" 
                  onClick={(e) => handleServiceClick(e, '#services')}
                  className="hover:text-white transition-colors cursor-pointer"
                >
                  Eliminación Virus Cartagena
                </a>
              </li>
              <li>
                <a 
                  href="#services" 
                  onClick={(e) => handleServiceClick(e, '#services')}
                  className="hover:text-white transition-colors cursor-pointer"
                >
                  Mantenimiento San Javier
                </a>
              </li>
              <li>
                <a 
                  href="#services" 
                  onClick={(e) => handleServiceClick(e, '#services')}
                  className="hover:text-white transition-colors cursor-pointer"
                >
                  Instalación Windows
                </a>
              </li>
              <li>
                <a 
                  href="#services" 
                  onClick={(e) => handleServiceClick(e, '#services')}
                  className="hover:text-white transition-colors cursor-pointer"
                >
                  Cursos IA Murcia
                </a>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h4 className="text-lg font-semibold mb-4">{t('footer.contact')}</h4>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center space-x-2">
                <SafeIcon icon={FiPhone} className="w-4 h-4 flex-shrink-0" />
                <a href="tel:+34642643499" className="hover:text-white transition-colors">+34 642 643 499</a>
              </li>
              <li className="flex items-center space-x-2">
                <SafeIcon icon={FiMail} className="w-4 h-4 flex-shrink-0" />
                <a href="mailto:info@tecnicoinformaticolamanga.com" className="hover:text-white transition-colors break-all text-sm">info@tecnicoinformaticolamanga.com</a>
              </li>
              <li className="flex items-start space-x-2">
                <SafeIcon icon={FiMapPin} className="w-4 h-4 flex-shrink-0 mt-1" />
                <span>La Manga del Mar Menor, San Javier 30380</span>
              </li>
            </ul>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="border-t border-gray-800 mt-12 pt-8"
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400">
                &copy; 2025 <a href="https://bertoproduction.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">BertoProduction.com</a> - {t('footer.rights')}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                NIF: Y/4514/312/R
              </p>
            </div>
            <div className="flex space-x-6">
              <Link 
                to="/privacy-policy" 
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                {t('footer.privacy')}
              </Link>
            </div>
          </div>
          <p className="mt-4 text-center text-gray-400 text-sm">
            {t('footer.areas')}
          </p>
          <p className="mt-2 text-center text-gray-500 text-xs">
            tecnicoinformaticomarmenor.com - Asistencia informática profesional 24/7
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;